"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.browserTracingIntegration = exports.BROWSER_TRACING_INTEGRATION_ID = void 0;
exports.getMetaContent = getMetaContent;
exports.startBrowserTracingNavigationSpan = startBrowserTracingNavigationSpan;
exports.startBrowserTracingPageLoadSpan = startBrowserTracingPageLoadSpan;

var _browserUtils = require("@sentry-internal/browser-utils");

var _core = require("@sentry/core");

var _debugBuild = require("../debug-build.js");

var _helpers = require("../helpers.js");

var _backgroundtab = require("./backgroundtab.js");

var _request = require("./request.js");

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/* eslint-disable max-lines */
const BROWSER_TRACING_INTEGRATION_ID = exports.BROWSER_TRACING_INTEGRATION_ID = 'BrowserTracing';

const DEFAULT_BROWSER_TRACING_OPTIONS = _objectSpread(_objectSpread({}, _core.TRACING_DEFAULTS), {}, {
  instrumentNavigation: true,
  instrumentPageLoad: true,
  markBackgroundSpan: true,
  enableLongTask: true,
  enableLongAnimationFrame: true,
  enableInp: true,
  _experiments: {}
}, _request.defaultRequestInstrumentationOptions);
/**
 * The Browser Tracing integration automatically instruments browser pageload/navigation
 * actions as transactions, and captures requests, metrics and errors as spans.
 *
 * The integration can be configured with a variety of options, and can be extended to use
 * any routing library.
 *
 * We explicitly export the proper type here, as this has to be extended in some cases.
 */


const browserTracingIntegration = (_options = {}) => {
  (0, _core.registerSpanErrorInstrumentation)();

  const _DEFAULT_BROWSER_TRAC = _objectSpread(_objectSpread({}, DEFAULT_BROWSER_TRACING_OPTIONS), _options),
        enableInp = _DEFAULT_BROWSER_TRAC.enableInp,
        enableLongTask = _DEFAULT_BROWSER_TRAC.enableLongTask,
        enableLongAnimationFrame = _DEFAULT_BROWSER_TRAC.enableLongAnimationFrame,
        _DEFAULT_BROWSER_TRAC2 = _DEFAULT_BROWSER_TRAC._experiments,
        enableInteractions = _DEFAULT_BROWSER_TRAC2.enableInteractions,
        enableStandaloneClsSpans = _DEFAULT_BROWSER_TRAC2.enableStandaloneClsSpans,
        beforeStartSpan = _DEFAULT_BROWSER_TRAC.beforeStartSpan,
        idleTimeout = _DEFAULT_BROWSER_TRAC.idleTimeout,
        finalTimeout = _DEFAULT_BROWSER_TRAC.finalTimeout,
        childSpanTimeout = _DEFAULT_BROWSER_TRAC.childSpanTimeout,
        markBackgroundSpan = _DEFAULT_BROWSER_TRAC.markBackgroundSpan,
        traceFetch = _DEFAULT_BROWSER_TRAC.traceFetch,
        traceXHR = _DEFAULT_BROWSER_TRAC.traceXHR,
        trackFetchStreamPerformance = _DEFAULT_BROWSER_TRAC.trackFetchStreamPerformance,
        shouldCreateSpanForRequest = _DEFAULT_BROWSER_TRAC.shouldCreateSpanForRequest,
        enableHTTPTimings = _DEFAULT_BROWSER_TRAC.enableHTTPTimings,
        instrumentPageLoad = _DEFAULT_BROWSER_TRAC.instrumentPageLoad,
        instrumentNavigation = _DEFAULT_BROWSER_TRAC.instrumentNavigation;

  const _collectWebVitals = (0, _browserUtils.startTrackingWebVitals)({
    recordClsStandaloneSpans: enableStandaloneClsSpans || false
  });

  if (enableInp) {
    (0, _browserUtils.startTrackingINP)();
  }

  if (enableLongAnimationFrame && _core.GLOBAL_OBJ.PerformanceObserver && PerformanceObserver.supportedEntryTypes && PerformanceObserver.supportedEntryTypes.includes('long-animation-frame')) {
    (0, _browserUtils.startTrackingLongAnimationFrames)();
  } else if (enableLongTask) {
    (0, _browserUtils.startTrackingLongTasks)();
  }

  if (enableInteractions) {
    (0, _browserUtils.startTrackingInteractions)();
  }

  const latestRoute = {
    name: undefined,
    source: undefined
  };
  /** Create routing idle transaction. */

  function _createRouteSpan(client, startSpanOptions) {
    const isPageloadTransaction = startSpanOptions.op === 'pageload';
    const finalStartSpanOptions = beforeStartSpan ? beforeStartSpan(startSpanOptions) : startSpanOptions;
    const attributes = finalStartSpanOptions.attributes || {}; // If `finalStartSpanOptions.name` is different than `startSpanOptions.name`
    // it is because `beforeStartSpan` set a custom name. Therefore we set the source to 'custom'.

    if (startSpanOptions.name !== finalStartSpanOptions.name) {
      attributes[_core.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE] = 'custom';
      finalStartSpanOptions.attributes = attributes;
    }

    latestRoute.name = finalStartSpanOptions.name;
    latestRoute.source = attributes[_core.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE];
    const idleSpan = (0, _core.startIdleSpan)(finalStartSpanOptions, {
      idleTimeout,
      finalTimeout,
      childSpanTimeout,
      // should wait for finish signal if it's a pageload transaction
      disableAutoFinish: isPageloadTransaction,
      beforeSpanEnd: span => {
        _collectWebVitals();

        (0, _browserUtils.addPerformanceEntries)(span, {
          recordClsOnPageloadSpan: !enableStandaloneClsSpans
        });
      }
    });

    function emitFinish() {
      if (['interactive', 'complete'].includes(_helpers.WINDOW.document.readyState)) {
        client.emit('idleSpanEnableAutoFinish', idleSpan);
      }
    }

    if (isPageloadTransaction && _helpers.WINDOW.document) {
      _helpers.WINDOW.document.addEventListener('readystatechange', () => {
        emitFinish();
      });

      emitFinish();
    }

    return idleSpan;
  }

  return {
    name: BROWSER_TRACING_INTEGRATION_ID,

    afterAllSetup(client) {
      let activeSpan;
      let startingUrl = _helpers.WINDOW.location && _helpers.WINDOW.location.href;

      function maybeEndActiveSpan() {
        if (activeSpan && !(0, _core.spanToJSON)(activeSpan).timestamp) {
          _debugBuild.DEBUG_BUILD && _core.logger.log(`[Tracing] Finishing current active span with op: ${(0, _core.spanToJSON)(activeSpan).op}`); // If there's an open active span, we need to finish it before creating an new one.

          activeSpan.end();
        }
      }

      client.on('startNavigationSpan', startSpanOptions => {
        if ((0, _core.getClient)() !== client) {
          return;
        }

        maybeEndActiveSpan();
        activeSpan = _createRouteSpan(client, _objectSpread({
          op: 'navigation'
        }, startSpanOptions));
      });
      client.on('startPageLoadSpan', (startSpanOptions, traceOptions = {}) => {
        if ((0, _core.getClient)() !== client) {
          return;
        }

        maybeEndActiveSpan();
        const sentryTrace = traceOptions.sentryTrace || getMetaContent('sentry-trace');
        const baggage = traceOptions.baggage || getMetaContent('baggage');
        const propagationContext = (0, _core.propagationContextFromHeaders)(sentryTrace, baggage);
        (0, _core.getCurrentScope)().setPropagationContext(propagationContext);
        activeSpan = _createRouteSpan(client, _objectSpread({
          op: 'pageload'
        }, startSpanOptions));
      }); // A trace should to stay the consistent over the entire time span of one route.
      // Therefore, when the initial pageload or navigation root span ends, we update the
      // scope's propagation context to keep span-specific attributes like the `sampled` decision and
      // the dynamic sampling context valid, even after the root span has ended.
      // This ensures that the trace data is consistent for the entire duration of the route.

      client.on('spanEnd', span => {
        const op = (0, _core.spanToJSON)(span).op;

        if (span !== (0, _core.getRootSpan)(span) || op !== 'navigation' && op !== 'pageload') {
          return;
        }

        const scope = (0, _core.getCurrentScope)();
        const oldPropagationContext = scope.getPropagationContext();
        scope.setPropagationContext(_objectSpread(_objectSpread({}, oldPropagationContext), {}, {
          sampled: oldPropagationContext.sampled !== undefined ? oldPropagationContext.sampled : (0, _core.spanIsSampled)(span),
          dsc: oldPropagationContext.dsc || (0, _core.getDynamicSamplingContextFromSpan)(span)
        }));
      });

      if (_helpers.WINDOW.location) {
        if (instrumentPageLoad) {
          startBrowserTracingPageLoadSpan(client, {
            name: _helpers.WINDOW.location.pathname,
            // pageload should always start at timeOrigin (and needs to be in s, not ms)
            startTime: _core.browserPerformanceTimeOrigin ? _core.browserPerformanceTimeOrigin / 1000 : undefined,
            attributes: {
              [_core.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'url',
              [_core.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.pageload.browser'
            }
          });
        }

        if (instrumentNavigation) {
          (0, _browserUtils.addHistoryInstrumentationHandler)(({
            to,
            from
          }) => {
            /**
             * This early return is there to account for some cases where a navigation transaction starts right after
             * long-running pageload. We make sure that if `from` is undefined and a valid `startingURL` exists, we don't
             * create an uneccessary navigation transaction.
             *
             * This was hard to duplicate, but this behavior stopped as soon as this fix was applied. This issue might also
             * only be caused in certain development environments where the usage of a hot module reloader is causing
             * errors.
             */
            if (from === undefined && startingUrl && startingUrl.indexOf(to) !== -1) {
              startingUrl = undefined;
              return;
            }

            if (from !== to) {
              startingUrl = undefined;
              startBrowserTracingNavigationSpan(client, {
                name: _helpers.WINDOW.location.pathname,
                attributes: {
                  [_core.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'url',
                  [_core.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.navigation.browser'
                }
              });
            }
          });
        }
      }

      if (markBackgroundSpan) {
        (0, _backgroundtab.registerBackgroundTabDetection)();
      }

      if (enableInteractions) {
        registerInteractionListener(idleTimeout, finalTimeout, childSpanTimeout, latestRoute);
      }

      if (enableInp) {
        (0, _browserUtils.registerInpInteractionListener)();
      }

      (0, _request.instrumentOutgoingRequests)(client, {
        traceFetch,
        traceXHR,
        trackFetchStreamPerformance,
        tracePropagationTargets: client.getOptions().tracePropagationTargets,
        shouldCreateSpanForRequest,
        enableHTTPTimings
      });
    }

  };
};
/**
 * Manually start a page load span.
 * This will only do something if a browser tracing integration integration has been setup.
 *
 * If you provide a custom `traceOptions` object, it will be used to continue the trace
 * instead of the default behavior, which is to look it up on the <meta> tags.
 */


exports.browserTracingIntegration = browserTracingIntegration;

function startBrowserTracingPageLoadSpan(client, spanOptions, traceOptions) {
  client.emit('startPageLoadSpan', spanOptions, traceOptions);
  (0, _core.getCurrentScope)().setTransactionName(spanOptions.name);
  const span = (0, _core.getActiveSpan)();
  const op = span && (0, _core.spanToJSON)(span).op;
  return op === 'pageload' ? span : undefined;
}
/**
 * Manually start a navigation span.
 * This will only do something if a browser tracing integration has been setup.
 */


function startBrowserTracingNavigationSpan(client, spanOptions) {
  (0, _core.getIsolationScope)().setPropagationContext({
    traceId: (0, _core.generateTraceId)()
  });
  (0, _core.getCurrentScope)().setPropagationContext({
    traceId: (0, _core.generateTraceId)()
  });
  client.emit('startNavigationSpan', spanOptions);
  (0, _core.getCurrentScope)().setTransactionName(spanOptions.name);
  const span = (0, _core.getActiveSpan)();
  const op = span && (0, _core.spanToJSON)(span).op;
  return op === 'navigation' ? span : undefined;
}
/** Returns the value of a meta tag */


function getMetaContent(metaName) {
  // Can't specify generic to `getDomElement` because tracing can be used
  // in a variety of environments, have to disable `no-unsafe-member-access`
  // as a result.
  // eslint-disable-next-line deprecation/deprecation
  const metaTag = (0, _core.getDomElement)(`meta[name=${metaName}]`); // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access

  return metaTag ? metaTag.getAttribute('content') : undefined;
}
/** Start listener for interaction transactions */


function registerInteractionListener(idleTimeout, finalTimeout, childSpanTimeout, latestRoute) {
  let inflightInteractionSpan;

  const registerInteractionTransaction = () => {
    const op = 'ui.action.click';
    const activeSpan = (0, _core.getActiveSpan)();
    const rootSpan = activeSpan && (0, _core.getRootSpan)(activeSpan);

    if (rootSpan) {
      const currentRootSpanOp = (0, _core.spanToJSON)(rootSpan).op;

      if (['navigation', 'pageload'].includes(currentRootSpanOp)) {
        _debugBuild.DEBUG_BUILD && _core.logger.warn(`[Tracing] Did not create ${op} span because a pageload or navigation span is in progress.`);
        return undefined;
      }
    }

    if (inflightInteractionSpan) {
      inflightInteractionSpan.setAttribute(_core.SEMANTIC_ATTRIBUTE_SENTRY_IDLE_SPAN_FINISH_REASON, 'interactionInterrupted');
      inflightInteractionSpan.end();
      inflightInteractionSpan = undefined;
    }

    if (!latestRoute.name) {
      _debugBuild.DEBUG_BUILD && _core.logger.warn(`[Tracing] Did not create ${op} transaction because _latestRouteName is missing.`);
      return undefined;
    }

    inflightInteractionSpan = (0, _core.startIdleSpan)({
      name: latestRoute.name,
      op,
      attributes: {
        [_core.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: latestRoute.source || 'url'
      }
    }, {
      idleTimeout,
      finalTimeout,
      childSpanTimeout
    });
  };

  if (_helpers.WINDOW.document) {
    addEventListener('click', registerInteractionTransaction, {
      once: false,
      capture: true
    });
  }
}